import { useState, useEffect } from 'react'
import { apiGet } from '../../services/api'
import { checkAndClearPeriodCache } from '../../services/offline'
import { BarChart3, Target, Award, AlertTriangle, Calendar } from 'lucide-react'
import LoadingSpinner from '../../components/common/LoadingSpinner'

interface Period {
  id: number
  year: number
  month: number
  name?: string
  is_locked: boolean
}

interface ScoreData {
  total_score: number
  is_eligible: boolean
  scores: Array<{
    indicator: { code: string; name: string; max_points: number }
    submission_count: number
    calculated_score: number
  }>
  disqualifications: Array<{
    disqualification_type: string
    description?: string
  }>
}

// Calculate current period based on 3rd of month cycle
const getCurrentPeriodRange = () => {
  const today = new Date()
  const day = today.getDate()
  const month = today.getMonth()
  const year = today.getFullYear()

  let startDate: Date
  let endDate: Date

  if (day >= 3) {
    // Current period: 3rd of this month to 3rd of next month
    startDate = new Date(year, month, 3)
    endDate = new Date(year, month + 1, 3)
  } else {
    // Previous period: 3rd of last month to 3rd of this month
    startDate = new Date(year, month - 1, 3)
    endDate = new Date(year, month, 3)
  }

  return { startDate, endDate, periodMonth: startDate.getMonth() + 1, periodYear: startDate.getFullYear() }
}

export default function ScoresPage() {
  const [scoreData, setScoreData] = useState<ScoreData | null>(null)
  const [periodRange, setPeriodRange] = useState(getCurrentPeriodRange())
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const periodsData = await apiGet<Period[]>('/kpi/periods')
        const range = getCurrentPeriodRange()
        setPeriodRange(range)

        // Find matching period from database
        const matchingPeriod = periodsData.find(
          p => p.month === range.periodMonth && p.year === range.periodYear
        ) || periodsData.find(p => !p.is_locked) || periodsData[0]

        if (matchingPeriod) {
          // Check if period changed and clear caches if needed
          await checkAndClearPeriodCache(matchingPeriod.id)

          // Fetch scores for this period
          const data = await apiGet<ScoreData>('/kpi/scores', { period_id: matchingPeriod.id })
          setScoreData(data)
        }
      } catch (error) {
        console.error('Failed to load data:', error)
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
  }, [])

  if (isLoading) {
    return <div className="flex items-center justify-center h-64"><LoadingSpinner size="lg" /></div>
  }

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900">My Scores</h1>

        {/* Period Display */}
        <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-100 px-3 py-2 rounded-lg">
          <Calendar className="h-4 w-4 text-gray-400" />
          <span>{formatDate(periodRange.startDate)} - {formatDate(periodRange.endDate)}</span>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <BarChart3 className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Score</p>
              <p className="text-3xl font-bold text-gray-900">{scoreData?.total_score?.toFixed(1) || 0}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <Target className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Required Score</p>
              <p className="text-3xl font-bold text-gray-900">80</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-4">
            <div className={`p-3 rounded-lg ${scoreData?.is_eligible ? 'bg-green-100' : 'bg-red-100'}`}>
              <Award className={`h-6 w-6 ${scoreData?.is_eligible ? 'text-green-600' : 'text-red-600'}`} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Eligibility</p>
              <p className={`text-lg font-bold ${scoreData?.is_eligible ? 'text-green-600' : 'text-red-600'}`}>
                {scoreData?.is_eligible ? 'Eligible' : 'Not Eligible'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Disqualifications */}
      {scoreData?.disqualifications && scoreData.disqualifications.length > 0 && (
        <div className="card border-red-200 bg-red-50">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <h3 className="font-semibold text-red-800">Disqualifications</h3>
          </div>
          <ul className="space-y-2">
            {scoreData.disqualifications.map((d, i) => (
              <li key={i} className="text-red-700">{d.disqualification_type}: {d.description || 'No details'}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Score breakdown */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Score Breakdown</h3>
        {scoreData?.scores && scoreData.scores.length > 0 ? (
          <div className="space-y-3">
            {scoreData.scores.map((score) => (
              <div key={score.indicator.code} className="border-b border-gray-100 pb-3 last:border-0 last:pb-0">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="flex-1 min-w-0">
                    <span className="text-xs font-medium text-primary-600">{score.indicator.code}</span>
                    <p className="text-sm text-gray-700 truncate">{score.indicator.name}</p>
                  </div>
                  <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">{score.calculated_score.toFixed(1)} pts</span>
                </div>
                <p className="text-xs text-gray-500">{score.submission_count} {score.submission_count === 1 ? 'submission' : 'submissions'}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <BarChart3 className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>No scores available for this period</p>
            <p className="text-sm mt-1">Submit KPI data to see your scores</p>
          </div>
        )}
      </div>
    </div>
  )
}
